import { type FormEvent } from 'react';
import { Button, Dialog, Input, Stack } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { constants } from '@eti/utils';

interface CancellationCertificateDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

type PersonalInfoType = {
  address?: string;
  city?: string;
  country?: string;
  zipCode?: string;
};

const CancellationCertificateDialog = ({ isOpen, onClose }: CancellationCertificateDialogProps) => {
  const { t } = useTranslation();

  const inputFields = [
    {
      label: t('OrderDetails.CancellationCertificate.Dialog.Address.Label'),
      name: 'address',
      maxChars: 128,
    },
    {
      label: t('OrderDetails.CancellationCertificate.Dialog.ZipCode.Label'),
      name: 'zipCode',
      maxChars: 30,
    },
    {
      label: t('OrderDetails.CancellationCertificate.Dialog.City.Label'),
      name: 'city',
      maxChars: 80,
    },
    {
      label: t('OrderDetails.CancellationCertificate.Dialog.Country.Label'),
      name: 'country',
      maxChars: 80,
    },
  ];

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const data: PersonalInfoType = Object.fromEntries(formData.entries());

    const params = Object.entries(data)
      .map(([key, value]) => (value ? `${key}=${encodeURIComponent(value)}` : ''))
      .join('&');

    onClose();

    window.open(
      `${window.location.origin}/cancellation-specification-certificate${
        params ? `?${params}` : ''
      }`,
    );
  };

  return (
    <Dialog
      closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
      data-testid="download-cancellation-certificate-dialog"
      isOpen={isOpen}
      onDismiss={onClose}
      title={t('OrderDetails.CancellationCertificate.Title')}
    >
      <Stack>
        <form onSubmit={handleSubmit}>
          <Stack>
            {inputFields.map((field) => (
              <Stack key={field.name} spacing={4}>
                <Input.Label htmlFor={field.name}>{field.label}</Input.Label>
                <Input
                  className={constants.MOUSE_FLOW_CLASS_NAMES}
                  data-testid={`${field.name}-input`}
                  id={field.name}
                  maxLength={field.maxChars}
                  name={field.name}
                  type="text"
                />
              </Stack>
            ))}
            <Button
              data-testid="download-cancellation-certificate-button"
              type="submit"
              variant="primary"
            >
              {t('OrderDetails.CancellationCertificate.Button.Text')}
            </Button>
          </Stack>
        </form>
      </Stack>
    </Dialog>
  );
};

export default CancellationCertificateDialog;
